import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import { useFinanceFiltersStore } from '../../stores/financeFiltersStore';
import type { FinancePeriodPreset } from './types';

/**
 * PeriodBar — control único de período del módulo Finanzas (Fase 2).
 *
 * Cuatro presets (7d / 30d / 90d / Mes actual) + rango personalizado.
 * Guarda el estado en `useFinanceFiltersStore`; los sub-componentes leen
 * `period`/`getQuery()` de ese mismo store para refetchear.
 */

const PRESETS: { key: FinancePeriodPreset; i18nKey: string }[] = [
  { key: '7d', i18nKey: 'finance.period.last7d' },
  { key: '30d', i18nKey: 'finance.period.last30d' },
  { key: '90d', i18nKey: 'finance.period.last90d' },
  { key: 'mtd', i18nKey: 'finance.period.mtd' },
];

const MAX_RANGE_DAYS = 365;

function daysBetween(from: string, to: string): number {
  const a = new Date(from + 'T00:00:00');
  const b = new Date(to + 'T00:00:00');
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function thirtyDaysAgoIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

export function PeriodBar() {
  const { t } = useTranslation();
  const { period, setPeriod } = useFinanceFiltersStore();
  const [showCustom, setShowCustom] = useState(period.mode === 'custom');
  const [from, setFrom] = useState<string>(
    period.mode === 'custom' ? period.from : thirtyDaysAgoIso(),
  );
  const [to, setTo] = useState<string>(
    period.mode === 'custom' ? period.to : todayIso(),
  );
  const [error, setError] = useState<string | null>(null);

  // Si cambian el período por afuera (otra pestaña, etc), sincronizar el form.
  useEffect(() => {
    if (period.mode === 'custom') {
      setFrom(period.from);
      setTo(period.to);
      setShowCustom(true);
    }
  }, [period]);

  const activePreset =
    period.mode === 'preset' ? period.preset : null;

  const choosePreset = (preset: FinancePeriodPreset) => {
    setShowCustom(false);
    setError(null);
    setPeriod({ mode: 'preset', preset });
  };

  const applyCustom = () => {
    if (!from || !to) {
      setError(t('finance.period.errRequired'));
      return;
    }
    if (from > to) {
      setError(t('finance.period.errOrder'));
      return;
    }
    const span = daysBetween(from, to);
    if (span > MAX_RANGE_DAYS) {
      setError(t('finance.period.errMax', { max: MAX_RANGE_DAYS }));
      return;
    }
    setError(null);
    setPeriod({ mode: 'custom', from, to });
  };

  return (
    <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-[var(--color-text-secondary)] mr-1">
          {t('finance.period.label')}:
        </span>

        {PRESETS.map((p) => {
          const active = activePreset === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => choosePreset(p.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors whitespace-nowrap ${
                active
                  ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
                  : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:text-[var(--color-text)]'
              }`}
            >
              {t(p.i18nKey)}
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => setShowCustom((v) => !v)}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors whitespace-nowrap ${
            period.mode === 'custom' || showCustom
              ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
              : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:text-[var(--color-text)]'
          }`}
        >
          <Calendar className="w-3.5 h-3.5" />
          {t('finance.period.custom')}
        </button>

        {period.mode === 'custom' && !showCustom && (
          <span className="text-xs text-[var(--color-text-secondary)] ml-1">
            {period.from} → {period.to}
          </span>
        )}
      </div>

      {showCustom && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="flex flex-col text-xs text-[var(--color-text-secondary)]">
            {t('finance.period.from')}
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              max={todayIso()}
              className="mt-1 px-2 py-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-[var(--color-text)] text-xs"
            />
          </label>
          <label className="flex flex-col text-xs text-[var(--color-text-secondary)]">
            {t('finance.period.to')}
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              max={todayIso()}
              className="mt-1 px-2 py-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-[var(--color-text)] text-xs"
            />
          </label>
          <button
            type="button"
            onClick={applyCustom}
            className="px-3 py-1.5 rounded-md text-xs font-medium bg-[var(--color-primary)] text-white hover:opacity-90"
          >
            {t('finance.period.apply')}
          </button>
          {error && (
            <span className="text-xs text-red-500 ml-2">{error}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default PeriodBar;
